import { lineQty } from './itemUnits.js';

/**
 * What a list has cost - the frontend side of the server's list_total /
 * spend_items (backend/qty_utils.py). V19.13, audit D18 and D19.
 *
 * A ticked item counts at item.price - what was paid. Ticking already writes
 * the shown price onto the item, so this is the number on the row. A price
 * that is only a suggestion (history, another store) may show on an unticked
 * row as a hint, but never counts here.
 */

const round2 = (n) => Math.round(n * 100) / 100;

/** One ticked line at what was paid; 0 when unticked or unpriced. */
export function paidLineTotal(item) {
  const p = Number(item?.price);
  return item?.checked && p > 0 ? p * lineQty(item) : 0;
}

/** Every ticked line added up, to the cent. */
export function tickedTotal(items) {
  return round2((items || []).reduce((s, i) => s + paidLineTotal(i), 0));
}

/** A total the user typed or a receipt captured (not one derived from items). */
export function isManualTotal(list) {
  return list?.total_price > 0 && list?.total_price_source !== 'auto';
}

/**
 * The one figure for Basket and Total Price Paid. A typed total stands; a
 * completed list reads the server's figure; a list still being shopped is its
 * ticked items, so a stale stored figure never shows.
 */
export function paidTotal(list) {
  if (isManualTotal(list) || list?.is_completed) return list?.total_price || 0;
  return tickedTotal(list?.items);
}

/**
 * Totals for the shared-list page. The server sends items_total, paid_total
 * and each item's line_total (list_total's rule); those are used as sent. The
 * fallback applies the same rule for a response without them.
 */
export function sharedTotals(list) {
  const items = list?.items || [];
  const lineTotal = (it) => {
    if (it && 'line_total' in it) return it.line_total;
    const p = Number(it?.price);
    return p > 0 ? round2(p * lineQty(it)) : null;
  };
  if (list && 'items_total' in list) {
    return { itemsTotal: list.items_total, paidTotal: list.paid_total ?? null, lineTotal };
  }
  // spend_items: ticked items; a completed list with nothing ticked counts all.
  const ticked = items.filter((i) => i.checked);
  const spend = list?.is_completed && ticked.length === 0 ? items : ticked;
  const itemsTotal = round2(spend.reduce((s, i) => {
    const p = Number(i.price);
    return s + (p > 0 ? p * lineQty(i) : 0);
  }, 0));
  const typedZero = list?.total_price === 0 && list?.total_price_source === 'manual';
  const paid = isManualTotal(list) || typedZero
    ? list.total_price
    : list?.is_completed ? (list.total_price > 0 ? list.total_price : itemsTotal) : null;
  return { itemsTotal, paidTotal: paid, lineTotal };
}

/**
 * The price an item is bought at when it is ticked: the one its row shows.
 * Returns the fields to write onto the item, or null when nothing changes.
 * (V19.19, ChatGPT 19.18 regression D28.)
 *
 * Until 19.19 only an item with NO price of its own took the row's price.
 * An item carried to a new list kept the old shop's A$3.30 while the row,
 * priced for the store the new list is at, showed that store's A$2.50 - so
 * the line and "Added item prices" said 2.50 and Basket and Total Price
 * Paid said 3.30. A price typed on this list for this store is already the
 * shown price (it is saved under that store), so this never changes it.
 *
 *   item   - the list item
 *   shown  - { val } the row shows for it, or null when the row is blank
 *   store  - the store the row is priced for, or null for none
 */
export function boughtAtPrice(item, shown, store) {
  if (!item) return null;
  // The row is BLANK at this store but the item carries another shop's
  // price (V19.20, Ivan): ticking it must not count that price as paid -
  // the line and Added item prices show nothing for it, so Basket and Total
  // Price Paid don't either. It is bought unpriced; the other shop's price
  // stays under that shop. ONLY when the price is on record under another
  // shop: a price with no shop at all (an imported list) is left as it was.
  // (A free item's row shows A$0.00, not blank.)
  if (!shown && store && Number(item.price) > 0) {
    const own0 = Number(item.price);
    const elsewhere = Object.entries(item.store_prices || {})
      .some(([s, v]) => s !== store && Math.abs(Number(v) - own0) < 0.005);
    return elsewhere ? { price: 0 } : null;
  }
  if (!shown || !(Number(shown.val) > 0)) return null;
  const own = Number(item.price);
  if (own > 0 && (!store || Math.abs(own - Number(shown.val)) < 0.005)) return null;
  return {
    price: Number(shown.val),
    ...(store ? { store_prices: { ...(item.store_prices || {}), [store]: Number(shown.val) } } : {}),
  };
}
